import { LEGACY_URL_REDIRECTS } from '../configurations/legacy-url-redirects.config';
import { LegacyUrlRedirectRule, ParsedRedirectRule } from '../models/url/url.model';

function parseUrl(url: string, baseUrl?: string): URL | null {
  try {
    return new URL(url, baseUrl);
  } catch {
    return null;
  }
}

function parseRedirectRule(url: string, baseUrl?: string): ParsedRedirectRule | null {
  const hasProtocol: boolean = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
  const normalizedUrl: URL | null = parseUrl(hasProtocol ? url : `https://${url}`, baseUrl);

  if (!normalizedUrl) {
    return null;
  }

  return { url: normalizedUrl, hasProtocol };
}

function buildRedirectUrl(currentUrl: URL, redirectRule: LegacyUrlRedirectRule): string | null {
  const legacyUrl: ParsedRedirectRule | null = parseRedirectRule(redirectRule.oldUrl);
  const targetUrl: ParsedRedirectRule | null = parseRedirectRule(redirectRule.newUrl, currentUrl.origin);

  if (!legacyUrl || !targetUrl) {
    return null;
  }

  const isExactMatch: boolean = currentUrl.href === legacyUrl.url.href;
  const isHostnameMatch: boolean = legacyUrl.url.pathname === '/' && legacyUrl.url.search === '' && legacyUrl.url.hash === '' && (
    currentUrl.origin === legacyUrl.url.origin ||
    currentUrl.host === legacyUrl.url.host ||
    currentUrl.hostname === legacyUrl.url.hostname
  );

  if (!isExactMatch && !isHostnameMatch) {
    return null;
  }

  const nextProtocol: string = targetUrl.hasProtocol ? targetUrl.url.protocol : currentUrl.protocol;
  const nextUrl: URL = new URL(`${nextProtocol}//${targetUrl.url.host}`);

  nextUrl.pathname = isExactMatch && targetUrl.url.pathname !== '/' ? targetUrl.url.pathname : currentUrl.pathname;
  nextUrl.search = currentUrl.search;
  nextUrl.hash = currentUrl.hash;

  return nextUrl.href;
}

export function redirectLegacyUrls(windowRef?: Window | null): boolean {
  const activeWindow: Window | null = windowRef ?? (typeof window === 'undefined' ? null : window);

  if (!activeWindow?.location?.href) {
    return false;
  }

  const currentUrl: URL = parseUrl(activeWindow.location.href);

  if (!currentUrl) {
    return false;
  }

  for (const redirectRule of LEGACY_URL_REDIRECTS) {
    const targetUrl: string = buildRedirectUrl(currentUrl, redirectRule);

    if (!targetUrl || targetUrl === currentUrl.href) {
      continue;
    }

    activeWindow.location.replace(targetUrl);
    return true;
  }

  return false;
}
